/**
 * IMT Notifications Component
 * 
 * Corporate-ready notification center for IMT users.
 * Displays notifications with filtering, search, and action buttons.
 * 
 * Features:
 * - Tabbed interface (All Notifications, Leave Requests)
 * - List view with filters (status, type, priority)
 * - Mark as read/archived functionality
 * - Delete notifications
 * - Search notifications
 * - Stats display with counts per tab
 * - Responsive design (table on desktop, cards on mobile)
 * 
 * Tabs:
 * - All Notifications: Shows all notification types
 * - Leave Requests: Filters to show only leave_request notifications
 */

import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import notificationService from '../services/notificationService';
import Footer from './ui/Footer';
import Layout from './Layout';
import { useFlashMessage } from './FlashMessage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBell,
  faBellSlash,
  faCheckCircle,
  faEye,
  faTrash,
  faArchive,
  faExclamationCircle,
  faExclamationTriangle,
  faCalendarCheck,
  faList,
  faCheck,
  faTimes,
  faClock,
  faClipboardCheck,
  faDollarSign,
  faTruck,
  faCogs,
  faShieldHalved
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../utils/axiosConfig';
import { getErrorMessage } from '../utils/errorMessages';
import { getAbsoluteMediaUrl } from '../utils/mediaUtils';
import HMSNotificationModal from './ui/HMSNotificationModal';
import HMSPageHeader from './ui/HMSPageHeader';
import HMSButton from './ui/HMSButton';
import HMSBadge from './ui/HMSBadge';
import HMSEmptyState from './ui/HMSEmptyState';
import HMSSkeleton from './ui/HMSSkeleton';
import HMSTable from './ui/HMSTable';
import HamsterFloatingButton from './HamsterFloatingButton';
import BackToTopButton from './BackToTopButton';
import './HMSNotifications.css';
import './HMSNotifications-TodoExtension.css';
import { costCentreBellTarget } from '../constants/costCentreBells';

function HMSNotifications() {
  const { user } = useContext(AuthContext);
  const { showSuccess, showError, showInfo } = useFlashMessage();
  const history = useHistory();
  
  // State
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [currentUserAvatar, setCurrentUserAvatar] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'leave-requests', 'todo-reminders', 'crm-notifications', 'delivery-approvals' or 'financial-approvals'
  
  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all'
  });
  
  // Modal state
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Leave request status tracking
  const [leaveRequestStatuses, setLeaveRequestStatuses] = useState({});

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch current user avatar
  useEffect(() => {
    const fetchCurrentUserAvatar = async () => {
      try {
        const profileResponse = await axiosInstance.get('/api/profile/me/');
        console.log('🖼️ Full Profile Response:', profileResponse.data);
        
        // API returns data nested in success/data structure
        const profileData = profileResponse.data.data || profileResponse.data;
        console.log('🖼️ Profile Data:', profileData);
        console.log('🖼️ Avatar Photo Field:', profileData.avatar_photo);
        
        if (profileData.avatar_photo) {
          const absoluteAvatarUrl = getAbsoluteMediaUrl(profileData.avatar_photo);
          console.log('🖼️ Absolute Avatar URL:', absoluteAvatarUrl);
          setCurrentUserAvatar(absoluteAvatarUrl);
        }
        setCurrentUserData(profileData);
      } catch (error) {
        console.error('Error fetching user avatar:', error);
      }
    };

    fetchCurrentUserAvatar();
  }, []);

  // Fetch notifications on component mount and filter changes
  useEffect(() => {
    fetchNotifications();
    fetchStats();
  }, [filters, activeTab]);

  // Listen for notification changes (e.g., when leaves are cancelled)
  useEffect(() => {
    const handleNotificationChange = () => {
      console.log('Notification change event detected, refetching notifications...');
      fetchNotifications();
      fetchStats();
    };
    
    window.addEventListener('notificationStatsChanged', handleNotificationChange);
    
    return () => {
      window.removeEventListener('notificationStatsChanged', handleNotificationChange);
    };
  }, [filters, activeTab]);

  /**
   * Fetch notifications from API
   */
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      
      // Apply tab-specific filtering
      const tabFilters = { ...filters };
      if (activeTab === 'leave-requests') {
        tabFilters.type = 'leave_request';
      } else if (activeTab === 'todo-reminders') {
        tabFilters.type = 'todo_reminder';
      } else if (activeTab === 'crm-notifications') {
        // For CRM tab, fetch all notifications and filter on frontend for both types
        // Since backend only supports single type filtering
        const allData = await notificationService.getNotifications(filters);
        const crmNotifications = allData.filter(n => 
          n.notification_type === 'crm_rfc_request' ||
          n.notification_type === 'crm_rfc_reviewed' ||
          n.notification_type === 'credit_approval_request' ||
          n.notification_type === 'credit_approval_approved' ||
          n.notification_type === 'credit_approval_rejected' ||
          n.notification_type === 'credit_handoff_blocked'
        );
        setNotifications(crmNotifications);
        setLoading(false);
        return;
      } else if (activeTab === 'financial-approvals') {
        // For Financial tab, fetch all notifications and filter on frontend for all financial types
        const allData = await notificationService.getNotifications(filters);
        const financialNotifications = allData.filter(n => 
          n.notification_type === 'financial_approval_request' || 
          n.notification_type === 'financial_approval_approved' ||
          n.notification_type === 'financial_approval_rejected' ||
          n.notification_type === 'invoice_approved' ||
          n.notification_type === 'invoice_rejected' ||
          n.notification_type === 'conversion_approval_request' ||
          n.notification_type === 'conversion_approved' ||
          n.notification_type === 'conversion_rejected'
        );
        setNotifications(financialNotifications);
        setLoading(false);
        return;
      } else if (activeTab === 'delivery-approvals') {
        // For Delivery Reviews tab, fetch all notifications and filter for delivery types
        const allData = await notificationService.getNotifications(filters);
        const deliveryNotifications = allData.filter(n => 
          n.notification_type === 'delivery_review_request' || 
          n.notification_type === 'delivery_comment_received'
        );
        setNotifications(deliveryNotifications);
        setLoading(false);
        return;
      } else if (activeTab === 'operations') {
        // For Operations tab, fetch all notifications and filter for operations access types
        const allData = await notificationService.getNotifications(filters);
        const operationsNotifications = allData.filter(n =>
          n.notification_type === 'ops_deal_created' ||
          n.notification_type === 'ops_recap_created' ||
          n.notification_type === 'ops_delivery_created' ||
          n.notification_type === 'ops_access_granted'
        );
        setNotifications(operationsNotifications);
        setLoading(false);
        return;
      } else if (activeTab === 'kyc-notifications') {
        // For KYC tab, fetch all notifications and filter for KYC clearance types
        const allData = await notificationService.getNotifications(filters);
        const kycNotifications = allData.filter(n =>
          n.notification_type === 'kyc_clearance_requested' ||
          n.notification_type === 'kyc_clearance_cleared'
        );
        setNotifications(kycNotifications);
        setLoading(false);
        return;
      }
      
      const data = await notificationService.getNotifications(tabFilters);
      setNotifications(data);
      
      // Fetch leave request statuses for leave notifications
      await fetchLeaveRequestStatuses(data);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      showError('Failed to load notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch notification statistics
   */
  const fetchStats = async () => {
    try {
      const statsData = await notificationService.getStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  /**
   * Fetch leave request statuses for all leave notifications
   */
  const fetchLeaveRequestStatuses = async (notificationList) => {
    try {
      // Get all leave request IDs from notifications
      const leaveRequestIds = notificationList
        .filter(n => n.notification_type === 'leave_request' && n.metadata?.leave_request_id)
        .map(n => n.metadata.leave_request_id);
      
      if (leaveRequestIds.length === 0) {
        return;
      }
      
      // Fetch all leave requests
      const response = await axiosInstance.get('/api/leave/admin/requests/');
      const leaveRequests = response.data.results || response.data;
      
      // Build a map of leave_request_id -> status
      const statusMap = {};
      leaveRequests.forEach(lr => {
        statusMap[lr.id] = lr.status;
      });
      
      setLeaveRequestStatuses(statusMap);
    } catch (err) {
      console.error('Error fetching leave request statuses:', err);
      // Don't show error to user - this is non-critical
    }
  };

  /**
   * Handle marking a notification as read
   */
  const handleMarkAsRead = async (notificationId) => {
    try {
      const response = await notificationService.markAsRead(notificationId);
      
      // Update local state directly - no reload needed
      setNotifications(prev => prev.map(notif => 
        notif.id === notificationId 
          ? { ...notif, status: 'read', status_display: 'Read' }
          : notif
      ));
      
      // Update stats locally
      setStats(prev => prev ? {
        ...prev,
        unread: Math.max(0, prev.unread - 1),
        read: prev.read + 1
      } : prev);
      
      // Dispatch custom event to update navbar badge immediately
      window.dispatchEvent(new CustomEvent('notificationStatsChanged'));
      
      showSuccess('Notification marked as read');
    } catch (err) {
      console.error('Error marking notification as read:', err);
      showError('Failed to mark notification as read');
    }
  };

  /**
   * Handle marking a notification as unread
   */
  const handleMarkAsUnread = async (notificationId) => {
    try {
      const response = await notificationService.markAsUnread(notificationId);
      
      // Update local state directly - no reload needed
      setNotifications(prev => prev.map(notif => 
        notif.id === notificationId 
          ? { ...notif, status: 'unread', status_display: 'Unread' }
          : notif
      ));
      
      // Update stats locally
      setStats(prev => prev ? {
        ...prev,
        unread: prev.unread + 1,
        read: Math.max(0, prev.read - 1)
      } : prev);
      
      // Dispatch custom event to update navbar badge immediately
      window.dispatchEvent(new CustomEvent('notificationStatsChanged'));
      
      showSuccess('Notification marked as unread');
    } catch (err) {
      console.error('Error marking notification as unread:', err);
      showError('Failed to mark notification as unread');
    }
  };

  /**
   * Handle toggling read/unread status
   */
  const handleToggleReadStatus = async (e, notification) => {
    e.stopPropagation(); // Prevent row click from firing
    
    if (notification.status === 'unread') {
      await handleMarkAsRead(notification.id);
    } else if (notification.status === 'read') {
      await handleMarkAsUnread(notification.id);
    }
  };

  /**
   * Handle row click to open modal
   *
   * Credit-approval notifications skip the generic modal and route directly
   * to the dedicated approval screen so the approver can act in one click.
   * Decision notifications (approved / rejected) route the requester back to
   * the partner profile so they can see the outcome in context.
   */
  const handleRowClick = async (notification) => {
    if (notification.status === 'unread') {
      // Fire-and-forget; UI navigates regardless.
      handleMarkAsRead(notification.id);
    }

    if (notification.notification_type === 'credit_approval_request') {
      const reqId = notification.metadata?.credit_request_id;
      if (reqId) {
        history.push(`/dashboard/crm/credit-approvals/${reqId}`);
        return;
      }
    }

    if (
      notification.notification_type === 'credit_approval_approved' ||
      notification.notification_type === 'credit_approval_rejected'
    ) {
      const profileId = notification.metadata?.profile_id;
      if (profileId) {
        history.push(`/dashboard/crm/profile/${profileId}`);
        return;
      }
    }

    // Credit approved but the deal is still in Draft (row 137): open the deal
    // itself, where the trader completes what is missing.
    if (notification.notification_type === 'credit_handoff_blocked') {
      if (notification.action_url) {
        history.push(notification.action_url);
        return;
      }
    }

    // KYC clearance notifications route straight to their action_url:
    // the approver lands on the KYC review screen, the requester on the
    // enquiry that has just been cleared for recap.
    if (
      notification.notification_type === 'kyc_clearance_requested' ||
      notification.notification_type === 'kyc_clearance_cleared'
    ) {
      if (notification.action_url) {
        history.push(notification.action_url);
        return;
      }
    }

    // Operations access-granted notifications deep-link straight to the Trade
    // Ops surface the operator was granted — their deals/recaps/deliveries live
    // there, so open it directly rather than the generic detail modal.
    if (notification.notification_type === 'ops_access_granted') {
      if (notification.action_url) {
        history.push(notification.action_url);
        return;
      }
    }

    // IMT only (divergence 07): the cost centre made for a new delivery. The
    // approval request opens the Cost Centres screen on that cost centre
    // (?waiting=1#cc-<id>); a failure opens the screen; a rejection opens the
    // delivery it was made for.
    const costCentreTarget = costCentreBellTarget(notification);
    if (costCentreTarget) {
      history.push(costCentreTarget);
      return;
    }

    // Contract sent to Legal / reviewed by Legal: Legal opens the contract,
    // the sender opens the recap it belongs to (both carry
    // metadata.legal_contract_id and their own action_url).
    if (notification.metadata?.legal_contract_id && notification.action_url) {
      history.push(notification.action_url);
      return;
    }

    setSelectedNotification(notification);
    setIsModalOpen(true);
  };
  
  /**
   * Handle modal close
   */
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedNotification(null);
  };
  
  /**
   * Handle action complete from modal
   */
  const handleActionComplete = () => {
    // Refresh notifications and stats
    fetchNotifications();
    fetchStats();
    
    // Dispatch custom event to update navbar badge immediately
    window.dispatchEvent(new CustomEvent('notificationStatsChanged'));
  };

  /**
   * Handle marking all notifications as read
   */
  const handleMarkAllAsRead = async () => {
    try {
      const result = await notificationService.markAllAsRead();
      showSuccess(result.message || 'All notifications marked as read');
      fetchNotifications();
      fetchStats();
      
      // Dispatch custom event to update navbar badge immediately
      window.dispatchEvent(new CustomEvent('notificationStatsChanged'));
    } catch (err) {
      console.error('Error marking all as read:', err);
      showError('Failed to mark all notifications as read');
    }
  };

  /**
   * Handle archiving a notification
   */
  const handleArchive = async (notificationId) => {
    try {
      await notificationService.markAsArchived(notificationId);
      
      // Find the notification to get its current status
      const notification = notifications.find(n => n.id === notificationId);
      
      // Update local state directly - no reload needed
      setNotifications(prev => prev.map(notif => 
        notif.id === notificationId 
          ? { ...notif, status: 'archived', status_display: 'Archived' }
          : notif
      ));
      
      // Update stats locally based on previous status
      if (notification) {
        setStats(prev => prev ? {
          ...prev,
          archived: prev.archived + 1,
          ...(notification.status === 'unread' && { unread: Math.max(0, prev.unread - 1) }),
          ...(notification.status === 'read' && { read: Math.max(0, prev.read - 1) })
        } : prev);
      }
      
      showSuccess('Notification archived');
    } catch (err) {
      console.error('Error archiving notification:', err);
      showError('Failed to archive notification');
    }
  };

  /**
   * Handle deleting a notification
   */
  const handleDelete = async (notificationId) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      // Find the notification to get its status before deletion
      const notification = notifications.find(n => n.id === notificationId);
      
      await notificationService.deleteNotification(notificationId);
      
      // Remove from local state directly - no reload needed
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      
      // Update stats locally
      if (notification) {
        setStats(prev => prev ? {
          ...prev,
          total: Math.max(0, prev.total - 1),
          ...(notification.status === 'unread' && { unread: Math.max(0, prev.unread - 1) }),
          ...(notification.status === 'read' && { read: Math.max(0, prev.read - 1) }),
          ...(notification.status === 'archived' && { archived: Math.max(0, prev.archived - 1) })
        } : prev);
      }
      
      showSuccess('Notification deleted');
    } catch (err) {
      console.error('Error deleting notification:', err);
      showError('Failed to delete notification');
    }
  };

  /**
   * Handle approving a leave request
   */
  const handleApproveLeaveRequest = async (notification) => {
    if (!window.confirm('Are you sure you want to approve this leave request?')) {
      return;
    }

    try {
      const leaveRequestId = notification.metadata?.leave_request_id;
      if (!leaveRequestId) {
        showError('Leave request ID not found');
        return;
      }

      await axiosInstance.post(`/api/leave/admin/requests/${leaveRequestId}/approve/`, {
        admin_notes: ''
      });
      
      showSuccess('Leave request approved successfully');
      
      // Mark notification as read
      await notificationService.markAsRead(notification.id);
      
      // Refresh notifications
      fetchNotifications();
      fetchStats();
    } catch (err) {
      console.error('Error approving leave request:', err);
      showError(getErrorMessage(err, 'Notification operation failed. Please try again.'));
    }
  };

  /**
   * Handle rejecting a leave request
   */
  const handleDenyLeaveRequest = async (notification) => {
    const reason = window.prompt('Please provide a reason for rejecting this leave request (optional):');
    if (reason === null) {
      return; // User clicked cancel
    }

    try {
      const leaveRequestId = notification.metadata?.leave_request_id;
      if (!leaveRequestId) {
        showError('Leave request ID not found');
        return;
      }

      await axiosInstance.post(`/api/leave/admin/requests/${leaveRequestId}/reject/`, {
        admin_notes: reason || ''
      });
      
      showSuccess('Leave request rejected');
      
      // Mark notification as read
      await notificationService.markAsRead(notification.id);
      
      // Refresh notifications
      fetchNotifications();
      fetchStats();
    } catch (err) {
      console.error('Error rejecting leave request:', err);
      showError(getErrorMessage(err, 'Notification operation failed. Please try again.'));
    }
  };

  /**
   * Handle filter change
   */
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };


  /**
   * Get the tone props for a priority chip
   *
   * HMSBadge carries two priority tones and this screen has four.
   * `urgent` and `high` are the primitive's red, `normal` (and anything
   * unrecognised, as before) is its blue; only `low` has no equivalent, so
   * it keeps a screen tone class on the shared chip.
   */
  const getPriorityProps = (priority) => {
    if (priority === 'low') {
      return { className: 'notif-priority-low' };
    }
    return { priority: priority === 'urgent' || priority === 'high' ? 'high' : 'normal' };
  };

  /**
   * Check if notification requires action (for leave requests)
   * Action is required if:
   * 1. Leave request is pending approval OR
   * 2. Work assignment hasn't been completed yet (for both approval-required and notify-only leaves)
   * 
   * Action is NOT required if:
   * 1. Leave request has been cancelled by the employee
   * 2. Leave request has been approved/rejected
   */
  const requiresAction = (notification) => {
    // Only check for leave request notifications
    if (notification.notification_type !== 'leave_request') {
      return false;
    }
    
    // If we don't have action_required flag, assume it doesn't require action
    if (!notification.action_required) {
      return false;
    }
    
    // If leave request was cancelled, no action is required
    if (notification.metadata?.cancelled === true) {
      return false;
    }
    
    const leaveRequestId = notification.metadata?.leave_request_id;
    if (!leaveRequestId) {
      return false;
    }
    
    const leaveStatus = leaveRequestStatuses[leaveRequestId];
    
    // For approval-required leaves: action required if status is pending
    if (notification.metadata?.requires_approval !== false) {
      return leaveStatus === 'pending';
    }
    
    // For notify-only (auto-approved) leaves: action required if work assignment doesn't exist
    if (notification.metadata?.requires_approval === false) {
      return notification.metadata?.work_assignment_exists !== true;
    }
    
    return false;
  };

  /**
   * Get counts for each tab
   */
  const getTabCounts = () => {
    const allCount = stats?.total || 0;
    // Use type-specific counts from stats API instead of filtering notifications array
    const leaveRequestsCount = stats?.leave_requests_count || 0;
    const todoRemindersCount = stats?.todo_reminders_count || 0;
    const crmRfcRequestCount = stats?.crm_rfc_request_count || 0;
    const crmRfcReviewedCount = stats?.crm_rfc_reviewed_count || 0;
    const crmNotificationsCount = crmRfcRequestCount + crmRfcReviewedCount;
    
    // Financial approvals count (from stats or calculate from notifications)
    const financialApprovalRequestCount = stats?.financial_approval_request_count || 0;
    const financialApprovalApprovedCount = stats?.financial_approval_approved_count || 0;
    const financialApprovalRejectedCount = stats?.financial_approval_rejected_count || 0;
    const invoiceApprovedCount = stats?.invoice_approved_count || 0;
    const invoiceRejectedCount = stats?.invoice_rejected_count || 0;
    const conversionApprovalRequestCount = stats?.conversion_approval_request_count || 0;
    const conversionApprovedCount = stats?.conversion_approved_count || 0;
    const conversionRejectedCount = stats?.conversion_rejected_count || 0;
    const financialApprovalsCount = financialApprovalRequestCount + financialApprovalApprovedCount + financialApprovalRejectedCount + invoiceApprovedCount + invoiceRejectedCount + conversionApprovalRequestCount + conversionApprovedCount + conversionRejectedCount;
    
    // Delivery reviews count
    const deliveryReviewRequestCount = stats?.delivery_review_request_count || 0;
    const deliveryCommentReceivedCount = stats?.delivery_comment_received_count || 0;
    const deliveryApprovalsCount = deliveryReviewRequestCount + deliveryCommentReceivedCount;
    
    // Operations access counts
    const opsDealCreatedCount = stats?.ops_deal_created_count || 0;
    const opsRecapCreatedCount = stats?.ops_recap_created_count || 0;
    const opsDeliveryCreatedCount = stats?.ops_delivery_created_count || 0;
    const opsAccessGrantedCount = stats?.ops_access_granted_count || 0;
    const operationsCount = opsDealCreatedCount + opsRecapCreatedCount + opsDeliveryCreatedCount + opsAccessGrantedCount;

    // KYC clearance counts
    const kycClearanceRequestedCount = stats?.kyc_clearance_requested_count || 0;
    const kycClearanceClearedCount = stats?.kyc_clearance_cleared_count || 0;
    const kycNotificationsCount = kycClearanceRequestedCount + kycClearanceClearedCount;

    return {
      all: allCount,
      leaveRequests: leaveRequestsCount,
      todoReminders: todoRemindersCount,
      crmNotifications: crmNotificationsCount,
      financialApprovals: financialApprovalsCount,
      deliveryApprovals: deliveryApprovalsCount,
      operations: operationsCount,
      kycNotifications: kycNotificationsCount
    };
  };

  /**
   * Format date for display
   */
  // The notifications table, on the shared HMSTable. Classes land on the
  // body cells only (cellProps), as the hand-made table had them.
  const notificationColumns = [
    {
      key: 'status',
      label: 'Status',
      cellProps: (notification) => ({
        className: 'status-icon-cell',
        onClick: (e) => handleToggleReadStatus(e, notification),
      }),
    },
    { key: 'priority', label: 'Priority' },
    { key: 'title', label: 'Title', cellProps: () => ({ className: 'notification-title' }) },
    { key: 'description', label: 'Description', cellProps: () => ({ className: 'notification-description' }) },
    { key: 'date', label: 'Date', cellProps: () => ({ className: 'notification-date' }) },
    {
      key: 'actions',
      label: 'Actions',
      cellProps: () => ({ className: 'actions-cell', onClick: (e) => e.stopPropagation() }),
    },
  ];

  const renderNotificationCell = (notification, column) => {
    switch (column.key) {
      case 'status':
        if (notification.status === 'unread') {
          return (
            <div className="status-indicator-unread" title="Click to mark as read">
              <div className="floating-circle"></div>
            </div>
          );
        }
        if (notification.status === 'read') {
          return (
            <div className="status-indicator-read" title="Click to mark as unread">
              <div className="read-circle">
                <FontAwesomeIcon icon={faCheck} />
              </div>
            </div>
          );
        }
        /* Archived (and anything unrecognised) is not one of the
           primitive's document states, so it takes the neutral base chip. */
        return <HMSBadge>{notification.status_display}</HMSBadge>;
      case 'priority':
        return (
          <HMSBadge {...getPriorityProps(notification.priority)}>
            {notification.priority_display}
          </HMSBadge>
        );
      case 'title':
        return notification.title;
      case 'description':
        return notification.description;
      case 'date':
        return formatDate(notification.created_at);
      case 'actions':
        return (
          <HMSButton
            variant="icon"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(notification);
            }}
            className={`notif-action-btn ${requiresAction(notification) ? 'notif-action-btn-attention' : 'notif-action-btn-view'}`}
            title={requiresAction(notification) ? 'Action Required' : 'View Details'}
          >
            <FontAwesomeIcon icon={requiresAction(notification) ? faExclamationTriangle : faEye} />
          </HMSButton>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabCounts = getTabCounts();
  const notificationTabs = [
    { key: 'all', label: 'All Notifications', icon: faList, count: tabCounts.all, showZero: true },
    { key: 'leave-requests', label: 'Leave Requests', icon: faCalendarCheck, count: tabCounts.leaveRequests, showZero: true },
    { key: 'todo-reminders', label: 'Todo Reminders', icon: faClock, count: tabCounts.todoReminders, showZero: true },
    { key: 'crm-notifications', label: 'CRM Notifications', icon: faClipboardCheck, count: tabCounts.crmNotifications, showZero: true },
    { key: 'financial-approvals', label: 'Financials', icon: faDollarSign, count: tabCounts.financialApprovals, showZero: true },
    { key: 'delivery-approvals', label: 'Deliveries', icon: faTruck, count: tabCounts.deliveryApprovals, showZero: true },
    { key: 'operations', label: 'Operations', icon: faCogs, count: tabCounts.operations, showZero: true },
    { key: 'kyc-notifications', label: 'KYC', icon: faShieldHalved, count: tabCounts.kycNotifications, showZero: true },
  ];

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="notifications-container">
        {/* Shared screen header: brand block, pill tray, stats + avatar cluster */}
        <HMSPageHeader
          title="Notifications"
          kicker="Workspace"
          tabs={{ items: notificationTabs, activeKey: activeTab, onChange: setActiveTab }}
          actions={
            <>
              {stats && (
                <div className="header-stats-compact">
                  <div className="stat-card-compact stat-unread">
                    <div className="stat-value-compact">{stats.unread}</div>
                    <div className="stat-label-compact">Unread</div>
                  </div>
                  <div className="stat-card-compact stat-priority">
                    <div className="stat-value-compact">{stats.high_priority}</div>
                    <div className="stat-label-compact">Priority</div>
                  </div>
                </div>
              )}
              <div className="header-user-avatar">
                {currentUserAvatar && (
                  <img
                    src={currentUserAvatar}
                    alt="Your profile"
                    className="user-avatar-image"
                    onError={(e) => {
                      console.error('Failed to load avatar image:', currentUserAvatar);
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                )}
                <div
                  className="user-avatar-initials"
                  style={{ display: currentUserAvatar ? 'none' : 'flex' }}
                >
                  {currentUserData?.first_name?.[0] || ''}{currentUserData?.last_name?.[0] || ''}
                </div>
              </div>
            </>
          }
        />
  
       {/* Compact Filters - Right Aligned */}
      <div className="filters-search-compact-container">
        <div className="filters-compact-group">
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="filter-select-compact"
          >
            <option value="all">All Status</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>

          <select
            value={filters.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
            className="filter-select-compact"
          >
            <option value="all">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Notifications Content */}
      <div className="notifications-content">
        {loading ? (
          /* Skeleton in the shape of the notification table that is coming. */
          <div className="notifications-loading">
            <HMSSkeleton shape="title" />
            <HMSSkeleton lines={6} />
          </div>
        ) : notifications.length === 0 ? (
          /* No `variant` — the block this replaces sat straight on the page
             gradient with no surface of its own. `size="3x"` is dropped: the
             primitive owns the 48px icon size. */
          <HMSEmptyState
            icon={<FontAwesomeIcon icon={faBellSlash} />}
            title="No notifications"
            hint="You're all caught up!"
          />
        ) : (
          /* HMSTable brings the sticky header and the 600px scroll viewport
             this screen used to set itself; the unread tint and the phone
             card layout stay on `.notifications-table`. The status and
             actions cells keep their own click handling, as before. */
          <HMSTable
            columns={notificationColumns}
            rows={notifications}
            rowKey={(notification) => notification.id}
            rowClassName={(notification) => `notification-row ${notification.status === 'unread' ? 'unread-row' : ''}`}
            onRowClick={handleRowClick}
            renderCell={renderNotificationCell}
            stickyHeader
            maxHeight={600}
            label="Notifications"
            className="notifications-table-container"
            tableClassName="notifications-table"
          />
        )}
      </div>

        {/* Hamster Chat Integration */}
        <HamsterFloatingButton />

        <Footer />
      </div>
      
      {/* Notification Detail Modal */}
      <HMSNotificationModal
        isOpen={isModalOpen}
        notification={selectedNotification}
        onClose={handleModalClose}
        onActionComplete={handleActionComplete}
      />
      
      {/* Back to Top Button */}
      <BackToTopButton />
    </Layout>
  );
}

export default HMSNotifications;
